﻿import React, { useState, useEffect, forwardRef, useRef } from "react";
import Checkbox from "@mui/material/Checkbox";
import styled from "@emotion/styled";
import "./ShepCheckbox.css";
import {
  CheckSelectedIcon,
  CheckHoverIcon,
  CheckIndeterminateIcon,
} from "@/ImageRepo";

const ShepCheckBox = styled("span")(({ theme, readOnly }) => ({
  width: 18,
  height: 18,
  border: "1px solid var(--neutral-5)",
  borderRadius: "2px",
  transition: "background-color 0.3s, backgroundImage 0.3s",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",

  "&::before": {
    content: '""',
    position: "absolute",
    width: "100%",
    height: "100%",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    opacity: 0,
    transition: "opacity 0.3s ease-out, border 0.3s ease-out",
  },

  "@keyframes borderEffect": {
    "0%": {
      border: "3px solid transparent",
      borderRadius: "5px",
      opacity: 0,
    },
    "75%": {
      border: "3px solid #b7c1c6",
      borderRadius: "5px",
      opacity: 1,
    },
    "100%": {
      border: "3px solid #b7c1c6",
      opacity: 0,
    },
  },

  "input:not(:checked):not(:disabled):not([data-indeterminate='true']):hover ~ &":
    !readOnly
      ? {
        border: "1px solid var(--status-upcoming-intense)",
        backgroundColor: "var(--default-light)",
        backgroundImage: `url("${CheckHoverIcon}")`,
      }
      : {},
  "input:disabled ~ &": {
    border: "1px solid var(--grey-light)",
    backgroundColor: "var(--neutral-2)",
  },
  "input:active ~ &": {
    "&::before": {
      borderRadius: "2px",
      margin: "-3px",
    },
  },
  "input:checked:not([data-indeterminate='true']) ~ &": {
    border: "1px solid var(--neutral-5)",
    backgroundColor: "var(--primary-main)",
    backgroundImage: `url("${CheckSelectedIcon}")`,
    opacity: 1,
    "&::before": {
      animation: "borderEffect 0.3s forwards",
    },
  },
  'input[data-indeterminate="true"] ~ &': {
    backgroundColor: "var(--primary-main)",
    "&::before": {
      backgroundImage: `url("${CheckIndeterminateIcon}")`,
      opacity: 1,
    },
  },
}));

const ShepCheckbox = forwardRef((props, ref) => {
  const [checked, setChecked] = useState(props.enabled === "true");
  const [indeterminate, setIndeterminate] = useState(
    props.indeterminate === "true"
  );
  const inputRef = useRef();

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
      inputRef.current.setAttribute("data-indeterminate", indeterminate);
    }
  }, [indeterminate]);

  const handleClick = () => {
    if (props.readOnly) return;
    if (indeterminate) {
      setIndeterminate(false);
      setChecked(false);
    } else {
      setChecked(!checked);
    }
    if (props.onClick) {
      props.onClick();
    }
  };

  const { onClick, readOnly, ...otherProps } = props;
  return (
    <Checkbox
      ref={inputRef}
      checked={checked}
      color="default"
      onClick={handleClick}
      icon={<ShepCheckBox readOnly={readOnly} />}
      checkedIcon={<ShepCheckBox readOnly={readOnly} />}
      indeterminateIcon={<ShepCheckBox readOnly={readOnly} />}
      inputProps={{ "aria-label": "Checkbox" }}
      sx={
        readOnly
          ? { cursor: "default", "&:hover": { backgroundColor: "transparent" } }
          : undefined
      }
      {...otherProps}
    />
  );
});

export default ShepCheckbox;
